import { useState } from "react";
import { Link } from "react-router-dom";
import { useT, LanguageToggle } from "../i18n";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { DaggryMark } from "../components/DaggryMark";

/**
 * /faq — public FAQ page. Bilingual (NO default + EN toggle), grouped by category.
 * Accordion-style expand/collapse, no external markdown — copy lives in this file.
 */

const SECTIONS = [
  {
    titleNb: "Komme i gang",
    titleEn: "Getting started",
    items: [
      {
        qNb: "Hva er EuroPilot, helt enkelt?",
        qEn: "What is EuroPilot, in plain English?",
        aNb: "EuroPilot er en Chrome-utvidelse som sier fra hver gang en norsk nettbutikk gir SAS EuroBonus-poeng eller Trumf-cashback — og hvilken rute som lønner seg mest. Den jobber stille i bakgrunnen mens du handler, og dukker opp som et lite ikon når den finner noe verdt å vite.",
        aEn: "EuroPilot is a Chrome extension that tells you whenever a Norwegian online store gives SAS EuroBonus points or Trumf cashback — and which route pays out the most. It works quietly in the background while you shop, and surfaces a small icon when it spots something worth knowing.",
      },
      {
        qNb: "Hvordan installerer jeg utvidelsen?",
        qEn: "How do I install the extension?",
        aNb: "Klikk \"Last ned utvidelsen\" på forsiden og følg stegene for Chrome Web Store. Etter installasjon må du logge inn én gang — så fungerer den på tvers av alle norske nettbutikker.",
        aEn: "Click \"Install extension\" on the homepage and follow the Chrome Web Store steps. After installing, log in once and EuroPilot works across every Norwegian online store.",
      },
      {
        qNb: "Hvorfor må jeg lage en konto?",
        qEn: "Why do I need to create an account?",
        aNb: "Innlogging knytter abonnementet til deg, ikke til én bestemt nettleser. Sånn kan du bruke EuroPilot på flere maskiner uten å betale flere ganger — og vi unngår at noen deler én konto med en hel familie.",
        aEn: "Logging in ties the subscription to you, not to a specific browser. That way you can use EuroPilot across multiple machines without paying twice — and we avoid one account being shared with an entire family.",
      },
    ],
  },
  {
    titleNb: "Abonnement og prøveperiode",
    titleEn: "Subscription & trial",
    items: [
      {
        qNb: "Hva koster det?",
        qEn: "What does it cost?",
        aNb: "29 kr per måned, inkludert mva. Nye kunder får introduksjonspris på 19 kr/mnd de første 2 månedene. Eller 299 kr/år (≈ 25 kr/mnd). Ingen oppstartsgebyr, ingen bindingstid. Du kan si opp når som helst.",
        aEn: "NOK 29 per month, including VAT. New customers get an introductory price of NOK 19/mo for the first 2 months. Or NOK 299/year (≈ 25 NOK/mo). No setup fee, no commitment. Cancel anytime.",
      },
      {
        qNb: "Hva er forskjellen på månedlig og årlig?",
        qEn: "What's the difference between monthly and yearly?",
        aNb: "Funksjonelt er det helt likt — du får tilgang til alle EuroPilot-funksjonene uansett hvilken du velger. Den årlige planen er rett og slett rabattert: du betaler 299 kr én gang i året i stedet for 29 kr × 12 = 348 kr. Du kan bytte til årlig fra kontosiden når som helst, og om du sier opp i prøveperioden trekkes ingenting.",
        aEn: "Functionally identical — you get every EuroPilot feature on either plan. The yearly plan is simply discounted: you pay NOK 299 once a year instead of 29 × 12 = NOK 348. You can switch to yearly from your account page at any time, and if you cancel during the trial nothing is charged.",
      },
      {
        qNb: "Hvordan fungerer 7-dagers gratis prøveperiode?",
        qEn: "How does the 7-day free trial work?",
        aNb: "Du registrerer kortet ditt hos Stripe når du oppretter konto, men ingenting belastes før dag 7. Hvis du sier opp innen prøveperioden, blir det heller ikke trukket noe. Får du vervet via en eksisterende bruker, får du 14 dagers gratis prøveperiode i stedet for 7.",
        aEn: "You register your card via Stripe at signup, but nothing is charged until day 7. Cancel within the trial and nothing is deducted. If you're referred by an existing user, your trial extends to 14 days instead of 7.",
      },
      {
        qNb: "Hvordan sier jeg opp?",
        qEn: "How do I cancel?",
        aNb: "Gå til kontosiden og trykk \"Administrer abonnement\" — du sendes til Stripe sin sikre kundeportal hvor du kan stoppe abonnementet med ett klikk. Du har tilgang ut perioden du allerede har betalt for.",
        aEn: "Head to your account page and click \"Manage subscription\" — you'll be sent to Stripe's secure customer portal where you can stop the subscription in one click. You retain access through the period you've already paid for.",
      },
      {
        qNb: "Kan jeg få refusjon?",
        qEn: "Can I get a refund?",
        aNb: "Hvis du har glemt å si opp og blitt belastet kort etter prøveperioden, send oss en e-post på hei@europilot.no innen 14 dager — så refunderer vi i full grad. For senere måneder må vi vurdere fra sak til sak.",
        aEn: "If you forgot to cancel and were charged shortly after the trial, email hei@europilot.no within 14 days and we'll refund in full. For later months we handle it case by case.",
      },
      {
        qNb: "Får jeg verveprovisjon?",
        qEn: "Do I get referral credit?",
        aNb: "Ja. Verver du 3 betalende venner via verve-lenken din på kontosiden, får du én måneds gratis EuroPilot. Lenken finner du under \"Verving\" i kontoen din.",
        aEn: "Yes. Refer 3 paying friends through your personal referral link (on your account page) and you get one month of EuroPilot free. The link lives under \"Referrals\" in your account.",
      },
    ],
  },
  {
    titleNb: "Funksjoner",
    titleEn: "Features",
    items: [
      {
        qNb: "Hva er \"beste rute\" mellom Trumf og SAS?",
        qEn: "What is the \"best route\" between Trumf and SAS?",
        aNb: "Mange butikker er med i både Trumf Netthandel og SAS Online Shopping, men gir ulik avkastning. EuroPilot regner ut hvilken rute som gir flest EuroBonus-poeng per krone du bruker — basert på dagens satser — og foreslår den ene som faktisk lønner seg. (Tips: ofte er det Trumf → konvertering til EuroBonus.)",
        aEn: "Many stores participate in both Trumf Netthandel and SAS Online Shopping, but with different payouts. EuroPilot calculates which route gives the most EuroBonus points per kroner spent — based on current rates — and surfaces the one that actually wins. (Hint: often it's Trumf → convert to EuroBonus.)",
      },
      {
        qNb: "Hvilke butikker støttes?",
        qEn: "Which stores are supported?",
        aNb: "Vi dekker hundrevis av norske nettbutikker — Komplett, XXL, Elkjøp, Meny, Hotels.com, IKEA, Zalando, Vinmonopolet, Booking, og mange flere. Databasen oppdateres ukentlig, og hvis du finner en butikk som mangler kan du rapportere den fra utvidelsen.",
        aEn: "We cover hundreds of Norwegian online stores — Komplett, XXL, Elkjøp, Meny, Hotels.com, IKEA, Zalando, Vinmonopolet, Booking, and many more. The database is updated weekly, and if you spot a missing store you can report it from inside the extension.",
      },
      {
        qNb: "Fungerer det på mobil?",
        qEn: "Does it work on mobile?",
        aNb: "Foreløpig kun på desktop Chrome (Windows, Mac, Linux) og Chromium-baserte nettlesere som Edge og Brave. Mobil-versjon vurderes — men nettbutikker behandler iOS-/Android-handel litt annerledes, så det krever sin egen arkitektur.",
        aEn: "Currently desktop Chrome only (Windows, Mac, Linux) and Chromium-based browsers like Edge and Brave. A mobile version is being explored — online stores treat iOS/Android shopping a bit differently, so it needs its own architecture.",
      },
      {
        qNb: "Kan jeg bytte språk?",
        qEn: "Can I switch language?",
        aNb: "Ja — bruk språk-bryteren oppe til høyre på nettsiden og i utvidelsen for å veksle mellom norsk og engelsk.",
        aEn: "Yes — use the language toggle at the top right of the website and inside the extension to switch between Norwegian and English.",
      },
    ],
  },
  {
    titleNb: "Personvern",
    titleEn: "Privacy",
    items: [
      {
        qNb: "Hvilke data samler dere?",
        qEn: "What data do you collect?",
        aNb: "Bare det vi trenger: e-postadresse, fornavn (valgfritt), kryptert passord og abonnementsstatus. Domener du skanner mellomlagres i 24 timer for å spare AI-kostnader. Vi lagrer aldri kortdetaljer (Stripe gjør det), og vi ser ikke hva du legger i kurven eller hva du kjøper.",
        aEn: "Only what we need: email, optional first name, encrypted password, and subscription status. Domains you scan are cached for 24 hours to save on AI costs. We never store card details (Stripe does), and we never see what you put in your cart or what you buy.",
      },
      {
        qNb: "Ser dere hvilke produkter jeg ser på?",
        qEn: "Do you see which products I look at?",
        aNb: "Nei. EuroPilot leser kun domenenavnet (f.eks. komplett.no) for å sjekke om butikken er med i et bonusprogram. Produktnavn, URL-parametere og kurv-innhold sendes aldri til serverne våre.",
        aEn: "No. EuroPilot only reads the domain name (e.g. komplett.no) to check if the store participates in a bonus program. Product names, URL parameters, and cart contents are never sent to our servers.",
      },
      {
        qNb: "Selger dere data?",
        qEn: "Do you sell my data?",
        aNb: "Aldri. Vi er et lite, norskeid produkt — inntekten kommer fra de 29 kronene i måneden, ikke fra å selge brukerdata. Personvern er et premiumprodukt for oss, ikke en kostnad.",
        aEn: "Never. We're a small Norwegian-owned product — revenue comes from the 29 NOK monthly fee, not from selling user data. Privacy is a premium product for us, not a cost.",
      },
    ],
  },
  {
    titleNb: "Feilsøking",
    titleEn: "Troubleshooting",
    items: [
      {
        qNb: "Utvidelsen sier \"ingen bonus funnet\" på en butikk jeg vet gir poeng — hva gjør jeg?",
        qEn: "The extension says \"no bonus found\" on a store I know gives points — what now?",
        aNb: "Trykk på rapportere-knappen i utvidelsen og fortell oss hvilken butikk det gjelder. Vi sjekker mot kilden og oppdaterer databasen — som regel innen et par dager.",
        aEn: "Hit the report button inside the extension and tell us which store. We verify against the source and update the database — usually within a couple of days.",
      },
      {
        qNb: "Poengene mine dukket aldri opp hos SAS — kan dere hjelpe?",
        qEn: "My points never showed up at SAS — can you help?",
        aNb: "Dessverre kun delvis. EuroPilot er et hjelpeverktøy — selve poengtildelingen håndteres av SAS EuroBonus eller Trumf. Hvis du mangler poeng, kontakt programmet direkte med kvittering. Vi kan veilede deg, men ikke etterregistrere på vegne av deg.",
        aEn: "Only partly. EuroPilot is a helper tool — actual point allocation is handled by SAS EuroBonus or Trumf. If you're missing points, contact the program directly with your receipt. We can guide you, but we can't retroactively register on your behalf.",
      },
      {
        qNb: "Jeg klarer ikke logge inn — hva nå?",
        qEn: "I can't log in — what now?",
        aNb: "Sjekk først at e-posten er riktig. Hvis 2FA-koden ikke kommer, sjekk søppelpost. Mister du tilgang til 2FA, bruk \"Mistet 2FA?\"-lenken under innloggingsskjemaet. Får du fortsatt ikke logget inn, send oss en e-post på hei@europilot.no.",
        aEn: "First check your email is correct. If the 2FA code doesn't arrive, check spam. Lost 2FA access? Use the \"Lost 2FA?\" link under the login form. Still can't log in? Email hei@europilot.no.",
      },
    ],
  },
];

function Item({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--srs-line-soft)" }} data-testid={`faq-item-${idx}`}>
      <button onClick={() => setOpen((v) => !v)}
              data-testid={`faq-toggle-${idx}`}
              style={{ width: "100%", textAlign: "left", padding: "18px 4px", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, color: "var(--srs-ink)", fontSize: 16, fontFamily: "inherit", fontWeight: 500, letterSpacing: "-0.01em" }}>
        <span style={{ flex: 1, lineHeight: 1.4 }}>{q}</span>
        <span style={{ color: "var(--srs-sun)", fontSize: 22, fontWeight: 300, transition: "transform 0.18s ease", transform: open ? "rotate(45deg)" : "rotate(0)" }}>+</span>
      </button>
      {open && (
        <div style={{ padding: "0 4px 22px", color: "var(--srs-ink-dim)", fontSize: 15, lineHeight: 1.7 }}>{a}</div>
      )}
    </div>
  );
}

export default function FAQ() {
  const { lang } = useT();
  const t = (nb, en) => (lang === "en" ? en : nb);

  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="faq-root">
      <nav style={{ position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--srs-bg) 88%, transparent)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--srs-line)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" }}>
            <DaggryMark size={28} />
            <span className="srs-display" style={{ fontSize: 22, fontWeight: 500 }}>EuroPilot</span>
          </Link>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <main style={{ flex: 1, maxWidth: 820, width: "100%", margin: "0 auto", padding: "60px 28px 80px" }}>
        <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 14 }}>// FAQ · {t("OFTE STILTE SPØRSMÅL", "FREQUENTLY ASKED QUESTIONS")}</div>
        <h1 className="srs-display" style={{ fontSize: 52, fontWeight: 500, letterSpacing: "-0.025em", margin: "0 0 18px 0", lineHeight: 1.02, color: "var(--srs-ink)" }}>
          {t("Spørsmål?", "Questions?")} <span style={{ color: "var(--srs-sun)", fontStyle: "italic", fontWeight: 400 }}>{t("Vi har svar.", "We've got answers.")}</span>
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: "var(--srs-ink-dim)", margin: "0 0 48px 0", maxWidth: 620 }}>
          {t(
            "Det viktigste om hvordan EuroPilot fungerer, hva det koster, hvilke data vi samler — og hvordan du kontakter oss hvis noe knirker.",
            "The essentials on how EuroPilot works, what it costs, what data we collect — and how to reach us if something's off."
          )}
        </p>

        {SECTIONS.map((sec) => (
          <section key={sec.titleNb} style={{ marginBottom: 48 }} data-testid={`faq-section-${sec.titleNb.toLowerCase().replace(/\s+/g, '-')}`}>
            <h2 style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.3em", color: "var(--srs-sun)", margin: "0 0 14px 0", fontWeight: 600, textTransform: "uppercase" }}>
              {t(sec.titleNb, sec.titleEn)}
            </h2>
            <div style={{ borderTop: "1px solid var(--srs-line-soft)" }}>
              {sec.items.map((it) => (
                <Item key={it.qNb} idx={it.qNb.toLowerCase().replace(/\s+/g, '-').slice(0, 40)} q={t(it.qNb, it.qEn)} a={t(it.aNb, it.aEn)} />
              ))}
            </div>
          </section>
        ))}

        <div style={{ marginTop: 64, padding: "32px 28px", background: "var(--srs-sand)", borderRadius: 20, textAlign: "center" }}>
          <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-sand-deep)", marginBottom: 8, fontWeight: 600 }}>// {t("FANT IKKE SVAR?", "DIDN'T FIND AN ANSWER?")}</div>
          <h3 className="srs-display" style={{ fontSize: 24, fontWeight: 500, margin: "0 0 8px 0", color: "var(--srs-ink)" }}>
            {t("Send oss en henvendelse.", "Send us a ticket.")}
          </h3>
          <p style={{ fontSize: 14, color: "var(--srs-ink-dim)", margin: "0 0 18px 0" }}>
            {t(
              "Logg inn og opprett en sak — vi svarer som regel innen 24 timer på hverdager.",
              "Log in and open a ticket — we usually reply within 24 hours on weekdays."
            )}
          </p>
          <Link to="/account/support" data-testid="faq-cta-support"
                style={{ display: "inline-block", padding: "11px 22px", background: "var(--srs-ink)", color: "var(--srs-bg)", textDecoration: "none", borderRadius: 999, fontSize: 13, fontWeight: 600 }}>
            {t("Åpne kundestøtte →", "Open support →")}
          </Link>
        </div>
      </main>

      <footer style={{ borderTop: "1px solid var(--srs-line)", padding: "24px 28px", textAlign: "center", fontSize: 12, color: "var(--srs-ink-faint)" }}>
        EuroPilot · Stavanger · <a href="mailto:hei@europilot.no" style={{ color: "var(--srs-sun)", textDecoration: "none" }}>hei@europilot.no</a> · <a href="mailto:corporate@europilot.no" style={{ color: "var(--srs-sun)", textDecoration: "none" }}>corporate@europilot.no</a>
      </footer>
    </div>
  );
}
